import React, { useState, useEffect } from 'react';
import type { ExpressionConstant } from '../../state/types';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export type ConstantForm = {
  name: string;
  type: 'number' | 'dayOfWeek';
  value: number | string;
};

type Props = {
  isOpen: boolean;
  existingNames?: string[];
  editingConstant?: ExpressionConstant | null;
  onSubmit: (form: ConstantForm, constantId?: string) => void;
  onCancel: () => void;
};

const AddConstantDialog: React.FC<Props> = ({
  isOpen,
  existingNames = [],
  editingConstant = null,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<'number' | 'dayOfWeek'>('number');
  const [numberValue, setNumberValue] = useState<string>('0');
  const [dayValue, setDayValue] = useState<string>('Monday');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setType('number');
      setNumberValue('0');
      setDayValue('Monday');
      setError(null);
      return;
    }
    if (editingConstant) {
      setName(editingConstant.name);
      setType(editingConstant.type);
      setNumberValue(
        editingConstant.type === 'number'
          ? String(editingConstant.value)
          : '0'
      );
      setDayValue(
        editingConstant.type === 'dayOfWeek'
          ? String(editingConstant.value)
          : 'Monday'
      );
    } else {
      setName('');
      setType('number');
      setNumberValue('0');
      setDayValue('Monday');
    }
    setError(null);
  }, [isOpen, editingConstant]);

  const canSubmit =
    name.trim() !== '' &&
    (type === 'number'
      ? numberValue.trim() !== '' && !Number.isNaN(parseFloat(numberValue))
      : true);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    const namesToCheck = editingConstant
      ? existingNames.filter((n) => n !== editingConstant.name)
      : existingNames;
    if (namesToCheck.some((n) => n.trim().toLowerCase() === trimmedName.toLowerCase())) {
      setError('A constant with this name already exists');
      return;
    }
    if (type === 'number') {
      const num = parseFloat(numberValue);
      if (Number.isNaN(num)) {
        setError('Enter a valid number');
        return;
      }
      onSubmit({ name: trimmedName, type: 'number', value: num }, editingConstant?.id);
    } else {
      onSubmit({ name: trimmedName, type: 'dayOfWeek', value: dayValue }, editingConstant?.id);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-newsprint-panel border border-rule rounded-sm p-8 w-96">
        <h2 className="text-xl font-bold text-ink mb-6 font-headline">
          {editingConstant ? 'Edit Constant' : 'Add Constant'}
        </h2>
        <form
          onSubmit={(e) => handleSubmit(e)}
          className="space-y-6"
        >
          <div>
            <label className="block text-sm font-medium text-ink mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="e.g. MaxBars"
              className="w-full px-4 py-2 border-2 border-rule rounded-sm text-ink bg-newsprint-panel focus:outline-none focus:border-ink"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-2">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'number' | 'dayOfWeek')}
              className="w-full px-4 py-2 border-2 border-rule rounded-sm text-ink bg-newsprint-panel focus:outline-none focus:border-ink"
            >
              <option value="number">Number</option>
              <option value="dayOfWeek">Day Of Week</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-2">
              Value
            </label>
            {type === 'number' ? (
              <input
                type="number"
                step="any"
                value={numberValue}
                onChange={(e) => setNumberValue(e.target.value)}
                className="w-full px-4 py-2 border-2 border-rule rounded-sm text-ink bg-newsprint-panel focus:outline-none focus:border-ink"
              />
            ) : (
              <select
                value={dayValue}
                onChange={(e) => setDayValue(e.target.value)}
                className="w-full px-4 py-2 border-2 border-rule rounded-sm text-ink bg-newsprint-panel focus:outline-none focus:border-ink"
              >
                {DAYS_OF_WEEK.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            )}
          </div>
          {error && (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 text-base font-medium rounded-sm bg-newsprint-dark hover:bg-newsprint transition text-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-8 py-3 text-base font-medium rounded-sm bg-ink text-newsprint hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Accept
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddConstantDialog;
